import React from 'react';

/**
 * ResponsiveAvatarThumbnail - A component that displays avatar thumbnails with responsive sizing
 * 
 * @param {Object} props
 * @param {string} props.avatarId - The avatar ID (e.g., 'ace', 'ada')
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.breakpoints - Custom breakpoints (optional)
 */
const ResponsiveAvatarThumbnail = ({ 
  avatarId, 
  alt, 
  className = '', 
  style = {}, 
  onClick,
  breakpoints = {
    small: { maxWidth: 768, width: 128 },
    medium: { maxWidth: 1024, width: 256 },
    large: { maxWidth: Infinity, width: 512 }
  }
}) => {
  // Generate responsive image sources
  const generateSrcSet = (size) => {
    const sizeConfig = breakpoints[size];
    return `/img/avatars-cache/${avatarId}/thumb/${avatarId}-${size}-${sizeConfig.width}w.webp ${sizeConfig.width}w`;
  };

  // Generate sizes attribute for responsive loading
  const generateSizes = () => {
    return Object.entries(breakpoints).map(([size, { maxWidth, width }]) => 
      maxWidth === Infinity 
        ? `${width}px` 
        : `(max-width: ${maxWidth}px) ${width}px`
    ).join(', ');
  };

  // Fallback to original image if optimized versions don't exist
  const fallbackSrc = `/img/avatars/${avatarId}/headshot/${avatarId}2.png`;

  return (
    <picture>
      {/* WebP sources for different sizes */}
      <source
        type="image/webp"
        srcSet={generateSrcSet('small')}
        sizes="128px"
        media="(max-width: 768px)"
      />
      <source
        type="image/webp"
        srcSet={generateSrcSet('medium')}
        sizes="256px"
        media="(max-width: 1024px)"
      />
      <source
        type="image/webp"
        srcSet={generateSrcSet('large')}
        sizes="512px"
      />
      
      {/* Fallback image */}
      <img
        src={fallbackSrc}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

export default ResponsiveAvatarThumbnail;
